import React from "react";
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Button
} from "@mui/material";
import { 
  SearchOff as SearchOffIcon,
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Help as HelpIcon
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import "../../../Landing.css";

function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={24}
          sx={{
            padding: { xs: 4, sm: 6 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            overflow: 'hidden',
            textAlign: 'center'
          }}
          className="fade-in"
        >
          {/* Декоративный градиент сверху */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: 'linear-gradient(90deg, #ff9a9e, #fecfef, #fecfef, #ff9a9e)'
            }}
          />

          {/* Иконка ошибки */}
          <Box
            sx={{
              width: 120,
              height: 120,
              background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
              animation: 'float 3s ease-in-out infinite',
              position: 'relative'
            }}
          >
            <SearchOffIcon sx={{ fontSize: 60, color: 'white' }} />
            <Box
              sx={{
                position: 'absolute',
                top: -5,
                right: -5,
                width: 40,
                height: 40,
                background: '#ff6b6b',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '1.2rem' }}>
                !
              </Typography>
            </Box>
          </Box>

          {/* Заголовок */}
          <Typography 
            variant="h1" 
            gutterBottom
            sx={{
              background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              marginBottom: 2,
              fontSize: { xs: '4rem', sm: '6rem' },
              lineHeight: 1
            }}
          >
            404
          </Typography>

          <Typography 
            variant="h4" 
            gutterBottom
            sx={{
              color: '#2c3e50',
              fontWeight: 'bold',
              marginBottom: 2,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
          >
            Страница не найдена
          </Typography>

          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              fontSize: '1.2rem', 
              marginBottom: 2,
              lineHeight: 1.6
            }}
          >
            Упс! Кажется, вы попали на страницу, которая не существует.
          </Typography>

          {/* URL который пытался открыть пользователь */}
          <Box
            sx={{
              backgroundColor: 'rgba(255, 154, 158, 0.1)',
              borderRadius: 2,
              padding: 2,
              marginBottom: 4,
              border: '1px solid rgba(255, 154, 158, 0.3)'
            }}
          >
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontStyle: 'italic', wordBreak: 'break-all' }}
            >
              Запрашиваемый адрес: <strong>{location.pathname}</strong>
            </Typography>
          </Box>

          {/* Возможные причины */}
          <Box
            sx={{
              textAlign: 'left',
              backgroundColor: 'rgba(102, 126, 234, 0.05)',
              borderRadius: 2,
              padding: 3,
              marginBottom: 4,
              borderLeft: '4px solid #0878ff'
            }}
          >
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#0878ff',
                marginBottom: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <HelpIcon /> Возможные причины:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                Опечатка в адресе страницы
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                Страница была перемещена или удалена
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                Ссылка устарела
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Требуется авторизация для доступа
              </Typography>
            </Box>
          </Box>

          {/* Кнопки действий */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
          >
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              sx={{
                background: 'linear-gradient(135deg, #0878ff 0%, #244a77 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                padding: '12px 24px',
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  boxShadow: '0 12px 35px rgba(102, 126, 234, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              На главную
            </Button>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                borderColor: '#0878ff',
                color: '#0878ff',
                fontWeight: 'bold',
                fontSize: '1rem',
                padding: '12px 24px',
                borderRadius: 3,
                textTransform: 'none',
                borderWidth: 2,
                '&:hover': {
                  borderColor: '#5a6fd8',
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  borderWidth: 2,
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Назад
            </Button>
          </Box>

          {/* Дополнительная помощь */}
          <Box
            sx={{
              marginTop: 4,
              padding: 2,
              backgroundColor: 'rgba(255, 154, 158, 0.05)',
              borderRadius: 2
            }}
          >
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontStyle: 'italic' }}
            >
              💡 <strong>Совет:</strong> Проверьте правильность написания адреса или воспользуйтесь навигацией сайта.
              Если проблема повторяется, обратитесь к администратору.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default NotFoundPage;
